import { useState, useEffect, useCallback, useMemo } from 'react';

interface AudioSettings {
  musicVolume: number; // 0-1 (for future use)
  soundEffectsVolume: number; // 0-1
  muted: boolean;
}

const DEFAULT_SETTINGS: AudioSettings = {
  musicVolume: 0.10, // 10% (for future background music)
  soundEffectsVolume: 0.5, // 50%
  muted: false,
};

const STORAGE_KEY = 'superwhere_audio_settings';

export function useAudioSettings() {
  const [settings, setSettings] = useState<AudioSettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (err) {
      console.error('Failed to load audio settings:', err);
    }
    return DEFAULT_SETTINGS;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (err) {
      console.error('Failed to save audio settings:', err);
    }
  }, [settings]);

  const setMusicVolume = useCallback((volume: number) => {
    setSettings(prev => ({ ...prev, musicVolume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  const setSoundEffectsVolume = useCallback((volume: number) => {
    setSettings(prev => ({ ...prev, soundEffectsVolume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    setSettings(prev => ({ ...prev, muted }));
  }, []);

  const toggleMute = useCallback(() => {
    setSettings(prev => ({ ...prev, muted: !prev.muted }));
  }, []);

  // Get effective volumes (0 if muted) - memoized to ensure reactivity
  const effectiveMusicVolume = useMemo(() => 
    settings.muted ? 0 : settings.musicVolume, 
    [settings.muted, settings.musicVolume]
  );
  const effectiveSoundEffectsVolume = useMemo(() => 
    settings.muted ? 0 : settings.soundEffectsVolume, 
    [settings.muted, settings.soundEffectsVolume]
  );

  return {
    ...settings,
    effectiveMusicVolume,
    effectiveSoundEffectsVolume,
    setMusicVolume,
    setSoundEffectsVolume,
    setMuted,
    toggleMute,
  };
}
